import { theme } from 'antd';

export const antdThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    // Primary Color Palette 
    colorPrimary: '#d16620', // --color-primary-600 (orange)
    colorSuccess: '#059669', // --color-success
    colorWarning: '#d97706', // --color-warning
    colorError: '#dc2626',   // --color-error
    colorInfo: '#0284c7',    // --color-info
    
    // Typography
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    fontSize: 16, // --font-size-base
    fontSizeHeading1: 48, // --font-size-4xl
    fontSizeHeading2: 36, // --font-size-3xl
    fontSizeHeading3: 30, // --font-size-2xl
    fontSizeHeading4: 24, // --font-size-xl
    fontSizeHeading5: 20, // --font-size-lg
    
    // Spacing and Layout
    sizeUnit: 4, // Base unit for spacing (4px)
    sizeStep: 4, // Step for size calculations
    borderRadius: 6, // --border-radius-md
    controlHeight: 40, // Standard control height
    
    // Colors
    colorBgBase: '#ffffff', // --color-background
    colorBgContainer: '#ffffff', // --color-surface
    colorBgElevated: '#fafafa', // --color-neutral-50
    colorBgLayout: '#fafafa', // --color-neutral-50
    colorBorder: '#e5e5e5', // --color-border
    colorBorderSecondary: '#f5f5f5', // --color-border-light
    colorText: '#171717', // --color-on-surface
    colorTextSecondary: '#525252', // --color-on-surface-muted
    colorTextTertiary: '#a3a3a3', // --color-neutral-400
    colorTextQuaternary: '#d4d4d4', // --color-neutral-300
    
    // Shadows
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)', // --shadow-md
    boxShadowSecondary: '0 1px 2px 0 rgb(0 0 0 / 0.05)', // --shadow-sm
    
    // Motion
    motionDurationFast: '0.15s', // --transition-fast
    motionDurationMid: '0.25s',  // --transition-normal
    motionDurationSlow: '0.35s', // --transition-slow
  },
  components: {
    // Layout Components
    Layout: {
      headerBg: '#ffffff',
      headerPadding: '0 24px',
      siderBg: '#fafafa',
      bodyBg: '#ffffff',
    },
    
    // Navigation
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: '#fdf9f5', // --color-primary-50
      itemSelectedColor: '#d16620', // --color-primary-600
      itemHoverBg: '#f5f5f5', // --color-neutral-100
      itemActiveBg: '#fbeee4', // --color-primary-100
    },
    
    // Form Components
    Input: {
      borderRadius: 6, // --border-radius-md
      paddingInline: 12,
      paddingBlock: 8,
      controlHeight: 40,
    },
    
    Button: {
      borderRadius: 6, // --border-radius-md
      controlHeight: 40,
      paddingInline: 16,
      fontWeight: 500, // --font-weight-medium
    },
    
    // Card Component
    Card: {
      borderRadius: 8, // --border-radius-lg
      paddingLG: 24,
      headerBg: 'transparent',
    },
    
    // Table Component
    Table: {
      borderRadius: 8, // --border-radius-lg
      headerBg: '#fafafa', // --color-neutral-50
      headerSplitColor: '#e5e5e5', // --color-border
    },
    
    // Modal Component
    Modal: {
      borderRadius: 8, // --border-radius-lg
      paddingLG: 24,
    },
    
    // Drawer Component
    Drawer: {
      paddingLG: 24,
    },
    
    // Notification and Message
    Notification: {
      borderRadius: 8, // --border-radius-lg
    },
    
    Message: {
      borderRadius: 6, // --border-radius-md
    },
  },
};

// Dark Theme Configuration
export const antdDarkThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    ...antdThemeConfig.token,
    
    // Dark Theme Colors
    colorBgBase: '#171717', // --color-neutral-900
    colorBgContainer: '#262626', // --color-neutral-800
    colorBgElevated: '#404040', // --color-neutral-700
    colorBgLayout: '#171717', // --color-neutral-900
    colorBorder: '#525252', // --color-border (dark)
    colorBorderSecondary: '#404040', // --color-border-light (dark)
    colorText: '#f5f5f5', // --color-on-surface (dark)
    colorTextSecondary: '#a3a3a3', // --color-on-surface-muted (dark)
    colorTextTertiary: '#737373', // --color-neutral-500
    colorTextQuaternary: '#525252', // --color-neutral-600
    
    // Dark Theme State Colors
    colorSuccess: '#10b981',
    colorWarning: '#f59e0b',
    colorError: '#ef4444',
    colorInfo: '#06b6d4',
  },
  components: {
    ...antdThemeConfig.components,
    
    Layout: {
      headerBg: '#262626', // --color-neutral-800
      siderBg: '#171717', // --color-neutral-900
      bodyBg: '#171717', // --color-neutral-900
    },
    
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: '#7a3d12', // --color-primary-900
      itemSelectedColor: '#fdb98c', // --color-primary-300
      itemHoverBg: '#404040', // --color-neutral-700
      itemActiveBg: '#b8571a', // --color-primary-700
    },
    
    Table: {
      headerBg: '#262626', // --color-neutral-800
      headerSplitColor: '#525252', // --color-border (dark)
    },
  },
};

// Theme provider utility
export const getAntdTheme = (isDark = false) => {
  return isDark ? antdDarkThemeConfig : antdThemeConfig;
};

// Component size configurations
export const componentSizes = {
  small: {
    controlHeight: 32,
    fontSize: 14,
    paddingInline: 12,
  },
  middle: {
    controlHeight: 40,
    fontSize: 16,
    paddingInline: 16,
  },
  large: {
    controlHeight: 48,
    fontSize: 18,
    paddingInline: 20,
  },
};

export default antdThemeConfig;